import { useEffect } from 'react';
import { normalizePhone, formatPhoneDisplay, MAX_PHONE_DIGITS } from './utils';
import style from './style.module.scss';
import FormInput from '../form-input';
import GenericButton from '../button-generic';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import { useStore } from '../../lib/hooks/useStore';
import { ReceiptType } from '../../lib/typegen/receipt-type';
import { observer } from 'mobx-react-lite';
import Confirm from '../confirm';
import { DigitalReceiptViewServerProps } from '../../lib/typegen/digital-receipt-view-server-props';
import logger from '../../lib/helpers/logger';
import { Views } from './types';
import { DigitalReceipt } from '../../lib/typegen/digital-receipt';
import api from '../../lib/api/api';
import { findReceiptPrintServerProps } from '../../lib/helpers/findConfigProps';

interface DigitalReceiptViewProps {
	receiptType: ReceiptType;
	digitalReceiptViewServerProps: DigitalReceiptViewServerProps;
}

const DigitalReceiptView = observer(function DigitalReceiptView({
	receiptType: passedReceiptType,
	digitalReceiptViewServerProps: {
		phoneEnterDataView,
		emailEnterDataView,
		confirmPhoneDataView,
		confirmEmailDataView,
		buttonLabelKey,
		confirmServerProps,
		phoneLabelKey,
		emailLabelKey,
	},
}: DigitalReceiptViewProps) {
	const {
		uiStore: { setTitle, setBackEnabled },
		accountStore: { setReceiptType, receiptType },
		configStore: { getString, config, currentLanguage },
		receiptStore: {
			digitalReceiptCurrentView: currentView,
			setDigitalReceiptCurrentView: setCurrentView,
			digitalReceiptCurrentData: currentData,
			setDigitalReceiptCurrentData: setCurrentData,
			digitalReceiptInputValue: inputValue,
			setDigitalReceiptInputValue: setInputValue,
			isDigitalReceiptConfirmButtonDisabled: disableConfirmButton,
			generateReceiptData,
		},
	} = useStore();

	useEffect(() => {
		setReceiptType(passedReceiptType);
	}, [passedReceiptType, setReceiptType]);

	const handleYesClick = async () => {
		if (disableConfirmButton || !inputValue) return;
		setBackEnabled(false);
		try {
			const receiptServerProps = findReceiptPrintServerProps(config);
			if (!receiptServerProps) {
				logger.logGeneral.error({}, 'Cannot send digital receipt: receipt print server props not found', 'no receipt print server props');
				setBackEnabled(true);
				return;
			}

			const generatedReceiptData = generateReceiptData(receiptServerProps);
			if (!generatedReceiptData) {
				logger.logGeneral.error({}, 'Cannot send digital receipt: receipt data generation returned no data', 'no receipt data');
				setBackEnabled(true);
				return;
			}

			if (receiptType === ReceiptType.Email) {
				const normalizedEmail = inputValue.trim().toLowerCase();
				const request: DigitalReceipt = {
					destination: normalizedEmail,
					receiptData: {
						headerData: generatedReceiptData.headerData,
						items: generatedReceiptData.items,
						footerData: generatedReceiptData.footerData,
					},
					language: currentLanguage,
				};
				await api.receipts.sendDigitalReceipt(receiptType, request);
				//setDigitalReceiptDestination(normalizedEmail);
			} else if (receiptType === ReceiptType.Text) {
				const digits = normalizePhone(inputValue);
				const request: DigitalReceipt = {
					destination: digits,
					receiptData: {
						headerData: generatedReceiptData.headerData,
						items: generatedReceiptData.items,
						footerData: generatedReceiptData.footerData,
					},
					language: currentLanguage,
				};
				await api.receipts.sendDigitalReceipt(receiptType, request);
				//setDigitalReceiptDestination(null, digits);
			}
		} catch (error) {
			logger.logGeneral.error({}, 'Failed to send digital receipt', error);
			setBackEnabled(true);
		}
	};

	useEffect(() => {
		if (receiptType === ReceiptType.Text)
			switch (currentView) {
				case Views.Enter:
					setTitle(getString(phoneEnterDataView.title));
					setCurrentData(phoneEnterDataView);
					break;
				case Views.Confirm:
					setTitle(getString(confirmPhoneDataView.title));
					setCurrentData(confirmPhoneDataView);
					break;
			}
		if (receiptType === ReceiptType.Email)
			switch (currentView) {
				case Views.Enter:
					setTitle(getString(emailEnterDataView.title));
					setCurrentData(emailEnterDataView);
					break;
				case Views.Confirm:
					setTitle(getString(confirmEmailDataView.title));
					setCurrentData(confirmEmailDataView);
					break;
			}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentView, receiptType]);

	const renderEnterDataView = () => (
		<section className={style.section}>
			<article className={style.section__description}>{currentData?.description && getString(currentData.description)}</article>
			{receiptType === ReceiptType.Email && (
				<FormInput
					className={style.section__input}
					type={'email'}
					name={'email'}
					dir='ltr'
					value={inputValue}
					onChange={(e) => {
						const next = e.target.value.replace(/\s/g, '');
						setInputValue(next);
					}}
					onBlur={() => setInputValue(inputValue.trim().toLowerCase())}
				/>
			)}
			{receiptType === ReceiptType.Text && (
				<FormInput
					className={style.section__input}
					type={'tel'}
					name={'phone'}
					dir='ltr'
					value={formatPhoneDisplay(normalizePhone(inputValue))}
					onChange={(e) => {
						const digitsOnly = normalizePhone(e.target.value).slice(0, MAX_PHONE_DIGITS);
						setInputValue(digitsOnly);
					}}
					onBlur={() => setInputValue(normalizePhone(inputValue).slice(0, MAX_PHONE_DIGITS))}
				/>
			)}
			<GenericButton
				disabled={disableConfirmButton}
				className={style.section__button}
				innerText={getString(buttonLabelKey)}
				isGrowHorizontally={false}
				icon={AppIconType.AcceptSolid}
				onClick={() => {
					logger.debug.log('Proceeding to confirm digital receipt view');
					setCurrentView(Views.Confirm);
				}}
			/>
		</section>
	);

	const renderConfirmDataView = () => (
		<section className={style.section}>
			{currentData?.description && <article className={style.section__description}>{getString(currentData.description)}</article>}
			<p className={style['section__confirm-text']}>
				{receiptType === ReceiptType.Email && `${getString(emailLabelKey)}: ${inputValue}`}
				{receiptType === ReceiptType.Text && `${getString(phoneLabelKey)}: ${formatPhoneDisplay(normalizePhone(inputValue))}`}
			</p>
			<Confirm
				{...confirmServerProps}
				className={style.section__confirm}
				noCallback={() => setCurrentView(Views.Enter)}
				yesCallback={async () => {
					await handleYesClick();
				}}
			/>
		</section>
	);

	const renderView = () => {
		switch (currentView) {
			case Views.Enter:
				return renderEnterDataView();
			case Views.Confirm:
				return renderConfirmDataView();
			default:
				return <></>;
		}
	};

	return renderView();
});

export default DigitalReceiptView;
